import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import supabase from "../config/supabaseClient";
import '../index2.css';

const ManagePlayerStatusPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const gameId = new URLSearchParams(location.search).get('gameId');

  const [summonedPlayers, setSummonedPlayers] = useState([]);
  const [firstTeam, setFirstTeam] = useState({});
  const [bench, setBench] = useState({});
  const [formation, setFormation] = useState("4-3-3");

  const formationLayouts = {
"4-3-3": [4, 3, 3],
    "4-4-2": [4, 4, 2],
    "3-5-2": [3, 5, 2],
    "3-4-3": [3, 4, 3],
    "4-2-3-1": [4, 2, 3, 1],
    "5-3-2": [5, 3, 2],
    "5-4-1": [5, 4, 1],
    "4-5-1": [4, 5, 1],
    "3-6-1": [3, 6, 1],
    "4-1-4-1": [4, 1, 4, 1],
    "4-1-3-2": [4, 1, 3, 2],
    "4-2-2-2": [4, 2, 2, 2],
    "3-3-4": [3, 3, 4],
    "4-3-2-1": [4, 3, 2, 1],
    "3-2-3-2": [3, 2, 3, 2],
    "3-2-2-3": [3, 2, 2, 3],
    "2-3-5": [2, 3, 5],
    "WM (3-2-2-3)": [3, 2, 2, 3],
    "4-2-4": [4, 2, 4],
    "3-4-1-2": [3, 4, 1, 2]
  };

  const benchSlotCount = 7;

  // Fetch game info to get formation
  useEffect(() => {
    const fetchGame = async () => {
      try {
        const { data: gameData, error } = await supabase
          .from('game')
          .select('formation')
          .eq('id', gameId)
          .single();

        if (error) throw error;
        if (gameData?.formation && formationLayouts[gameData.formation]) {
          setFormation(gameData.formation);
        }
      } catch (err) {
        console.error('Error fetching game formation:', err);
      }
    };

    fetchGame();
  }, [gameId]);

  // Fetch summoned players for the game
  useEffect(() => {
    const fetchSummonedPlayers = async () => {
      try {
        const { data: callData, error: callError } = await supabase
          .from('call_players_game')
          .select('player_id')
          .eq('game_id', gameId)
          .eq('call', 'summoned');
        if (callError) throw callError;

        const playerIds = callData.map(d => d.player_id);
        if (playerIds.length === 0) return setSummonedPlayers([]);

        const { data: players, error: playersError } = await supabase
          .from('players')
          .select()
          .in('id', playerIds);
        if (playersError) throw playersError;

        setSummonedPlayers(players);
      } catch (err) {
        console.error(err);
        setSummonedPlayers([]);
      }
    };

    fetchSummonedPlayers();
  }, [gameId]);

  const getSelectedPlayerIds = () => [
    ...Object.values(firstTeam).filter(Boolean),
    ...Object.values(bench).filter(Boolean),
  ];

  const handleAssign = (slotKey, playerId, type) => {
    const playerInt = playerId ? parseInt(playerId) : "";
    if (type === "first") {
      setFirstTeam(prev => ({ ...prev, [slotKey]: playerInt }));
      setBench(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(k => {
          if (updated[k] === playerInt) updated[k] = "";
        });
        return updated;
      });
    } else {
      setBench(prev => ({ ...prev, [slotKey]: playerInt }));
      setFirstTeam(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(k => {
          if (updated[k] === playerInt) updated[k] = "";
        });
        return updated;
      });
    }
  };

  const getAvailablePlayers = (currentSelection) => {
    const selectedIds = getSelectedPlayerIds().filter(id => id !== currentSelection);
    return summonedPlayers.filter(p => !selectedIds.includes(p.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const firstTeamInserts = Object.entries(firstTeam)
        .filter(([_, v]) => v)
        .map(([pos, player_id]) => ({
          game_id: gameId,
          player_id,
          status: "first",
          position: pos
        }));

      const benchInserts = Object.entries(bench)
        .filter(([_, v]) => v)
        .map(([pos, player_id]) => ({
          game_id: gameId,
          player_id,
          status: "bench",
          position: "Bench"
        }));

      const inserts = [...firstTeamInserts, ...benchInserts];

      const { error } = await supabase.from('status_player_game').upsert(inserts);
      if (error) throw error;

      navigate(`/game/${gameId}`);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="page manage-player-status">
      <h2>Manage Player Assignments - Game {gameId}</h2>

      <div className="form-group">
        <label>Formation:</label>
        <p>{formation}</p>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="mini-pitch">
          <div className="line goalkeeper">
            <PositionSlot
              slotKey="GK"
              value={firstTeam["GK"] || ""}
              summonedPlayers={getAvailablePlayers(firstTeam["GK"])}
              handleAssign={(k, v) => handleAssign(k, v, "first")}
            />
          </div>

          {formationLayouts[formation].map((count, lineIndex) => (
            <div key={lineIndex} className="line">
              {Array.from({ length: count }).map((_, i) => {
                const posKey = `${formation}-L${lineIndex + 1}-P${i + 1}`;
                return (
                  <PositionSlot
                    key={posKey}
                    slotKey={posKey}
                    value={firstTeam[posKey] || ""}
                    summonedPlayers={getAvailablePlayers(firstTeam[posKey])}
                    handleAssign={(k, v) => handleAssign(k, v, "first")}
                  />
                );
              })}
            </div>
          ))}
        </div>

        <div className="bench-section">
          <h3>Bench</h3>
          <div className="bench-slots">
            {Array.from({ length: benchSlotCount }).map((_, i) => {
              const benchKey = `B${i + 1}`;
              return (
                <PositionSlot
                  key={benchKey}
                  slotKey={benchKey}
                  value={bench[benchKey] || ""}
                  summonedPlayers={getAvailablePlayers(bench[benchKey])}
                  handleAssign={(k, v) => handleAssign(k, v, "bench")}
                />
              );
            })}
          </div>
        </div>

        <button type="submit">Submit Assignments</button>
      </form>

      <style>{`
        .mini-pitch {
          background: green;
          border: 2px solid white;
          border-radius: 10px;
          width: 300px;
          height: 400px;
          margin: 20px auto;
          display: flex;
          flex-direction: column;
          justify-content: space-around;
          padding: 10px;
        }
        .line {
          display: flex;
          justify-content: center;
          gap: 10px;
        }
        .player-slot select {
          font-size: 12px;
        }
        .bench-section {
          margin: 20px;
        }
        .bench-slots {
          display: flex;
          justify-content: center;
          gap: 10px;
          flex-wrap: wrap;
        }
      `}</style>
    </div>
  );
};

const PositionSlot = ({ slotKey, value, summonedPlayers, handleAssign }) => (
  <div className="player-slot">
    <select value={value} onChange={(e) => handleAssign(slotKey, e.target.value)}>
      <option value="">--</option>
      {summonedPlayers.map(p => (
        <option key={p.id} value={p.id}>{p.Name}</option>
      ))}
    </select>
  </div>
);

export default ManagePlayerStatusPage;
